/**
 * Planning poker rules that the UI needs: decks, who may do what, and the result maths.
 * The server enforces the same rules (server/pb_hooks/lib/poker.js); keep the decks in sync.
 */
import type {
  PlayersResponse,
  RoomsResponse,
  RoomsVotingSystemOptions,
  VotesResponse,
} from "@/lib/pb-types";

export type VotingSystem = RoomsVotingSystemOptions;

export const VOTING_SYSTEMS: Record<VotingSystem, { label: string; cards: string[] }> = {
  fibonacci: {
    label: "Fibonacci",
    cards: ["0", "1", "2", "3", "5", "8", "13", "21", "34", "55", "89", "?", "☕"],
  },
  modified_fibonacci: {
    label: "Modified Fibonacci",
    cards: ["0", "½", "1", "2", "3", "5", "8", "13", "20", "40", "100", "?", "☕"],
  },
  t_shirts: { label: "T-shirts", cards: ["XS", "S", "M", "L", "XL", "?", "☕"] },
  powers_of_2: {
    label: "Powers of 2",
    cards: ["0", "1", "2", "4", "8", "16", "32", "64", "?", "☕"],
  },
  linear: {
    label: "Linear",
    cards: ["1", "2", "3", "4", "5", "6", "7", "8", "9", "10", "?", "☕"],
  },
};

/** "( 0, 1, 2, 3, 5, … )", shown next to the deck name when creating a room. */
export function deckDescription(system: VotingSystem): string {
  return `( ${VOTING_SYSTEMS[system].cards.join(", ")} )`;
}

export function cardsFor(system: VotingSystem): string[] {
  return (VOTING_SYSTEMS[system] ?? VOTING_SYSTEMS.fibonacci).cards;
}

type Room = Pick<RoomsResponse, "admin" | "who_can_reveal" | "who_can_manage_issues">;

export function isAdmin(room: Room, userId: string | undefined): boolean {
  return Boolean(userId) && room.admin === userId;
}

export function canReveal(room: Room, userId: string | undefined): boolean {
  return room.who_can_reveal === "all" || isAdmin(room, userId);
}

export function canManageIssues(room: Room, userId: string | undefined): boolean {
  return room.who_can_manage_issues === "all" || isAdmin(room, userId);
}

/** Players shown at the table: everyone present, plus anyone who left after voting. */
export function seatedPlayers<T extends Pick<PlayersResponse, "active" | "has_voted">>(
  players: T[],
): T[] {
  return players.filter((player) => player.active || player.has_voted);
}

const NUMERIC: Record<string, number> = { "½": 0.5 };

/** Average of the numeric cards, one decimal ("4.5"), or null when there is none. */
export function calcAverage(values: string[]): string | null {
  const numbers = values
    .map((value) => NUMERIC[value] ?? Number.parseFloat(value))
    .filter((value) => !Number.isNaN(value));
  if (numbers.length === 0) return null;
  return (numbers.reduce((sum, value) => sum + value, 0) / numbers.length).toFixed(1);
}

/** [card, count] pairs, most picked first. */
export function voteDistribution(values: string[]): [string, number][] {
  const counts = new Map<string, number>();
  for (const value of values) if (value) counts.set(value, (counts.get(value) ?? 0) + 1);
  return [...counts.entries()].sort((a, b) => b[1] - a[1]);
}

/** The card each player picked, by player id. */
export function votesByPlayer(votes: Pick<VotesResponse, "player" | "value">[]) {
  return new Map(votes.map((vote) => [vote.player, vote.value]));
}
